// Estado de la verificación de calidad a nivel columna
// (ADR-2026-09-07-verificacion-de-calidad-en-la-columna). Un mapa por imagen con lo que
// dijo el backend, más dos mapas locales chicos: "recién toqué Analizar" (para que el
// velo aparezca antes de que /listar lo refleje) y "falló acá" (red, timeout). Todo lo
// que decide qué se dibuja está en estado.ts; acá solo hay red y tiempo.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GenerateAdError, mapErrorTypeToMessage, type GenerateAdErrorType } from '../../../lib/generateAdRequest';
import { ratioDeFormato } from '../../../utils/ratioDeFormato';
import type { GeneracionDelegada } from '../components/generacionDelegada';
import { pollGeneratedByExecutionId } from '../pollGeneratedByExecutionId';
import { analizarImagen, listarVerificacion, regenerarImagen } from './api';
import {
  estadoDeCard,
  idsParaAnalisisAutomatico,
  idsPorConocer,
  imagenesMalas,
  LOTE_LISTAR,
  type EstadoCard,
} from './estado';
import { marcarExplicada as marcar, yaExplicada } from './explicada';
import { lanzarRegeneraciones, type Lanzada } from './regenerarLote';
import { liberarLanzamiento, reservarLanzamiento } from '../lotes/lanzamientoUnico';
import type { ItemVerificacion, ModalidadVerificacion, ProductoDeImagen } from './tipos';

const POLL_MS = 3000;

export interface UseVerificacionParams {
  activa: boolean;
  companyId: string | null;
  /** Ids de las cards de imagen visibles, en orden de grilla (sin videos). */
  idsVisibles: readonly string[];
  tituloDe: (id: string) => string;
  onGeneracionDelegada?: (g: GeneracionDelegada) => void;
  /**
   * Analizar sola cada imagen recién generada, sin que el usuario toque el botón
   * (ADR-2026-09-07-analisis-automatico-al-generar).
   */
  automatico?: boolean;
  /**
   * Ids de las imágenes de las TANDAS de esta sesión: lo único que el modo automático
   * puede disparar. El historial se analiza a mano — prender la función no sale a
   * analizar (ni a pagar) las creaciones viejas.
   */
  idsDeTandas?: ReadonlySet<string>;
}

export interface VerificacionApi {
  estadoDe: (id: string) => EstadoCard;
  regenerableDe: (id: string) => boolean;
  /** Los productos de esa imagen: el popover abre el editor de uno para cargarle fotos. */
  productosDe: (id: string) => ProductoDeImagen[];
  /** Con qué pantalla se generó: decide si se puede ajustar antes de regenerar. */
  modoDe: (id: string) => string | null;
  /** Contra qué se midió esa imagen: decide la copy del resultado
   *  (ADR-2026-09-13-el-analisis-sin-producto-mira-las-referencias). */
  modalidadDe: (id: string) => ModalidadVerificacion | null;
  /** Lo que va a cobrar regenerar esa imagen; `null` si no se puede regenerar. */
  creditosDe: (id: string) => number | null;
  /** Lo que va a cobrar regenerar TODAS las malas visibles, sumado. */
  creditosDeMalas: number;
  /** La empresa dueña de las imágenes; `EditProductFlow` no abre sin esto. */
  companyId: string | null;
  /**
   * El host sabe DÓNDE aterriza lo regenerado (`onGeneracionDelegada`). Sin eso, el botón
   * de regenerar cobraría un crédito por una imagen que el usuario no vería aparecer en
   * ningún lado, así que no se ofrece (ADR-2026-09-07-verificacion-en-el-inline).
   */
  puedeRegenerar: boolean;
  /** Las malas (fidelidad ≤ 2) entre las visibles, en orden de grilla. */
  malas: string[];
  necesitaExplicacion: boolean;
  marcarExplicada: () => void;
  analizar: (id: string) => Promise<void>;
  regenerar: (id: string) => Promise<void>;
  regenerarMalas: () => Promise<void>;
  /** Hay una tanda de "regenerar las malas" en vuelo: el botón se apaga en TODAS las cards. */
  regenerandoLote: boolean;
  /** Último error de acción, ya mostrable; null si no hay. */
  error: string | null;
  limpiarError: () => void;
}

const mensajeDe = (e: unknown, fallback: string): string =>
  e instanceof GenerateAdError ? e.message : e instanceof Error && e.message ? e.message : fallback;

export function useVerificacion(p: UseVerificacionParams): VerificacionApi {
  const {
    activa,
    companyId,
    idsVisibles,
    tituloDe,
    onGeneracionDelegada,
    automatico = false,
    idsDeTandas,
  } = p;
  const [items, setItems] = useState<Map<string, ItemVerificacion>>(() => new Map());
  const [pendientes, setPendientes] = useState<Map<string, number>>(() => new Map());
  const [erroresLocales, setErroresLocales] = useState<Map<string, string>>(() => new Map());
  const [ahora, setAhora] = useState(() => Date.now());
  const [error, setError] = useState<string | null>(null);
  const [explicada, setExplicada] = useState(() => yaExplicada());
  // Ids ya pedidos (aunque la respuesta no haya llegado): evita pedir dos veces lo mismo
  // mientras la primera respuesta viaja.
  const pedidos = useRef<Set<string>>(new Set());
  /** Ids que el modo automático ya disparó en esta sesión: nunca se analizan dos veces. */
  const autoDisparados = useRef<Set<string>>(new Set());
  // Una sola tanda de "regenerar las malas" a la vez. El ref corta aunque todavía no haya
  // re-render; el estado es lo que apaga el botón en las demás cards.
  const loteEnCurso = useRef(false);
  const [regenerandoLote, setRegenerandoLote] = useState(false);

  const mergear = useCallback((nuevos: ItemVerificacion[]) => {
    setItems((prev) => {
      const next = new Map(prev);
      for (const it of nuevos) next.set(it.imagen_id, it);
      return next;
    });
    // Lo que el backend ya refleja (terminado o "analizando") deja de ser pendiente local.
    setPendientes((prev) => {
      if (prev.size === 0) return prev;
      const next = new Map(prev);
      for (const it of nuevos) if (it.analisis) next.delete(it.imagen_id);
      return next.size === prev.size ? prev : next;
    });
  }, []);

  // Al cambiar los visibles: pedir lo que no se conoce, en lotes.
  useEffect(() => {
    if (!activa || !companyId) return;
    const lotes = idsPorConocer(pedidos.current, idsVisibles);
    if (lotes.length === 0) return;
    for (const lote of lotes) for (const id of lote) pedidos.current.add(id);
    void (async () => {
      for (const lote of lotes) {
        try {
          const res = await listarVerificacion(lote);
          // Se mergea SIEMPRE, sin guarda de "sigue vivo"
          // (ADR-2026-09-07-strictmode-se-comia-la-respuesta). `pedidos` es un ref y
          // SOBREVIVE al ciclo montar-limpiar-remontar que StrictMode hace en desarrollo:
          // con la guarda, la primera pasada marcaba los ids y su respuesta se descartaba
          // al limpiar, y la segunda ya no pedía nada porque los veía pedidos. Resultado:
          // el mapa quedaba vacío para siempre y ninguna card mostraba su botón.
          // Si el componente se desmontó de verdad, este setState es un no-op en React 18.
          mergear(res);
        } catch (e) {
          // Se vuelve a intentar la próxima vez que cambien los visibles.
          for (const id of lote) pedidos.current.delete(id);
          console.warn('[verificacion] listar falló', e);
        }
      }
    })();
  }, [activa, companyId, idsVisibles, mergear]);

  // Ids que hay que seguir mirando: pendientes locales + los que el backend dice analizando.
  const enCurso = useMemo(() => {
    const out = new Set<string>(pendientes.keys());
    for (const [id, it] of items) if (it.analisis?.estado === 'analizando') out.add(id);
    return [...out];
  }, [items, pendientes]);

  // Poll cada 3 s mientras haya algo en curso. También refresca `ahora` (para el tope).
  useEffect(() => {
    if (!activa || enCurso.length === 0) return;
    let vivo = true;
    const tick = async () => {
      setAhora(Date.now());
      try {
        const res = await listarVerificacion(enCurso.slice(0, LOTE_LISTAR));
        if (vivo) mergear(res);
      } catch (e) {
        console.warn('[verificacion] poll falló', e);
      }
    };
    const t = window.setInterval(() => void tick(), POLL_MS);
    return () => {
      vivo = false;
      window.clearInterval(t);
    };
  }, [activa, enCurso, mergear]);

  const estadoDe = useCallback(
    (id: string): EstadoCard =>
      estadoDeCard({
        item: items.get(id),
        pendienteDesde: pendientes.get(id),
        errorLocal: erroresLocales.get(id),
        ahora,
      }),
    [items, pendientes, erroresLocales, ahora],
  );

  const regenerableDe = useCallback((id: string) => items.get(id)?.regenerable === true, [items]);

  const productosDe = useCallback((id: string) => items.get(id)?.productos ?? [], [items]);

  const modoDe = useCallback((id: string) => items.get(id)?.modo_utilizado ?? null, [items]);
  const modalidadDe = useCallback((id: string) => items.get(id)?.modalidad ?? null, [items]);

  const creditosDe = useCallback((id: string) => items.get(id)?.creditos ?? null, [items]);

  const malas = useMemo(() => imagenesMalas(items, idsVisibles), [items, idsVisibles]);

  /**
   * El lote no cuesta "una por imagen": cada una vale según la resolución con la que se
   * generó, así que se SUMA lo que dijo el backend por cada una
   * (ADR-2026-09-13-el-boton-de-regenerar-dice-cuanto-cuesta).
   */
  const creditosDeMalas = useMemo(
    () => malas.reduce((total, id) => total + (items.get(id)?.creditos ?? 1), 0),
    [items, malas],
  );

  const analizar = useCallback(async (id: string) => {
    setError(null);
    setErroresLocales((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
    setPendientes((prev) => new Map(prev).set(id, Date.now()));
    try {
      await analizarImagen(id);
    } catch (e) {
      setPendientes((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      setErroresLocales((prev) => new Map(prev).set(id, mensajeDe(e, 'No se pudo pedir el análisis.')));
    }
  }, []);

  /**
   * Modo automático: apenas `/listar` confirma que una imagen recién generada es
   * analizable, se le dispara el análisis sin esperar al usuario
   * (ADR-2026-09-07-analisis-automatico-al-generar).
   *
   * `autoDisparados` se marca ANTES de llamar: `analizar` deja la imagen como pendiente
   * LOCAL y eso no cambia `items`, así que sin la marca previa el mismo efecto volvería
   * a dispararla en el próximo render.
   */
  useEffect(() => {
    if (!activa || !automatico || !companyId || !idsDeTandas || idsDeTandas.size === 0) return;
    const pendientesDeAuto = idsParaAnalisisAutomatico({
      items,
      idsDeTandas,
      yaDisparados: autoDisparados.current,
    });
    for (const id of pendientesDeAuto) {
      autoDisparados.current.add(id);
      void analizar(id);
    }
  }, [activa, analizar, automatico, companyId, idsDeTandas, items]);

  /** La promesa que cierra la tanda: pollea por los execution_id y devuelve los ids entregados. */
  const resultadoDe = useCallback(
    async (lanzadas: Lanzada[]): Promise<string[]> => {
      if (!companyId) return [];
      const rows = await pollGeneratedByExecutionId({
        executionId: lanzadas.map((l) => l.execution_id),
        companyId,
        expectedCount: lanzadas.length,
      });
      const ok = rows.filter((r) => !r.has_error).map((r) => r.id);
      if (ok.length === 0) {
        const tipo = (rows.find((r) => r.has_error)?.error_type ?? 'technical') as GenerateAdErrorType;
        throw new GenerateAdError(tipo, mapErrorTypeToMessage(tipo), null);
      }
      return ok;
    },
    [companyId],
  );

  const delegar = useCallback(
    (lanzadas: Lanzada[]) => {
      if (lanzadas.length === 0 || !onGeneracionDelegada) return;
      const primera = lanzadas[0]!;
      onGeneracionDelegada({
        tipo: 'regenerar',
        total: lanzadas.length,
        ratio: ratioDeFormato(primera.formato) ?? 0.8,
        producto: lanzadas.length === 1 ? tituloDe(primera.imagen_id) : '',
        esVideo: false,
        origenId: primera.imagen_id,
        creditos: lanzadas.reduce((s, l) => s + l.creditos, 0),
        resultado: resultadoDe(lanzadas),
      });
    },
    [onGeneracionDelegada, resultadoDe, tituloDe],
  );

  const regenerar = useCallback(
    async (id: string) => {
      // El `ocupado` de la card es estado y el POST que cobra es el primer `await`: dos
      // clicks en "Regenerar imagen" en el mismo tick salían los dos y cobraban dos veces.
      // Mismo cerrojo por id que el lote (ADR-2026-09-14-generar-una-sola-vez-por-click).
      if (!reservarLanzamiento(`regenerar:${id}`)) return;
      try {
        setError(null);
        const { lanzadas, fallo } = await lanzarRegeneraciones([id], {
          regenerar: regenerarImagen,
          uuid: () => crypto.randomUUID(),
        });
        delegar(lanzadas);
        // El pedido ni salió: el texto va al popover (el modal de plan es del camino de la promesa).
        if (fallo) setError(mensajeDe(fallo.error, 'No se pudo regenerar la imagen. Intentá nuevamente.'));
      } finally {
        liberarLanzamiento(`regenerar:${id}`);
      }
    },
    [delegar],
  );

  // El lote es de la columna, no de la card: el popover se cierra solo (Escape o click
  // afuera) con la tanda todavía en vuelo, y el mismo botón de otra card volvería a lanzar
  // los MISMOS ids con nuevos execution_id — trabajo duplicado y créditos cobrados dos
  // veces. Por eso el candado vive acá y no en el `ocupado` de cada CapaVerificacion.
  const regenerarMalas = useCallback(async () => {
    if (loteEnCurso.current) return;
    const ids = malas;
    if (ids.length === 0) return;
    loteEnCurso.current = true;
    setRegenerandoLote(true);
    setError(null);
    try {
      const { lanzadas, fallo } = await lanzarRegeneraciones(ids, {
        regenerar: regenerarImagen,
        uuid: () => crypto.randomUUID(),
      });
      delegar(lanzadas);
      if (fallo) {
        const base = mensajeDe(fallo.error, 'No se pudieron regenerar todas.');
        setError(lanzadas.length > 0 ? `${base} Se lanzaron ${lanzadas.length} de ${ids.length}.` : base);
      }
    } finally {
      loteEnCurso.current = false;
      setRegenerandoLote(false);
    }
  }, [delegar, malas]);

  const marcarExplicada = useCallback(() => {
    marcar();
    setExplicada(true);
  }, []);
  const limpiarError = useCallback(() => setError(null), []);

  return {
    estadoDe,
    regenerableDe,
    productosDe,
    modoDe,
    modalidadDe,
    creditosDe,
    creditosDeMalas,
    companyId,
    puedeRegenerar: !!onGeneracionDelegada,
    malas,
    necesitaExplicacion: !explicada,
    marcarExplicada,
    analizar,
    regenerar,
    regenerarMalas,
    regenerandoLote,
    error,
    limpiarError,
  };
}
